export type CadenceSettings = {
  clicksPerLoop: number;
  secondsBetweenClicks: number;
  minutesBetweenLoops: number;
  numberOfLoops: number;
};

export type CadenceProgress = {
  loopNumber: number;
  clickNumber: number;
  totalLoops: number;
  clicksPerLoop: number;
};

export type CadenceRunResult = 'completed' | 'stopped';

type RunCadenceOptions = {
  settings: CadenceSettings;
  signal: AbortSignal;
  attempt: (progress: CadenceProgress) => void;
  onProgress?: (progress: CadenceProgress) => void;
  now?: () => number;
};

const MAX_TIMER_DELAY_MILLISECONDS = 2_147_483_647;

export function isValidCadenceSettings(settings: CadenceSettings): boolean {
  const millisecondsBetweenClicks = settings.secondsBetweenClicks * 1000;
  const millisecondsBetweenLoops = settings.minutesBetweenLoops * 60 * 1000;
  const scheduledLoopSpan = (settings.numberOfLoops - 1) * millisecondsBetweenLoops;

  return (
    Number.isSafeInteger(settings.clicksPerLoop) &&
    settings.clicksPerLoop > 0 &&
    Number.isFinite(settings.secondsBetweenClicks) &&
    settings.secondsBetweenClicks >= 0 &&
    Number.isFinite(millisecondsBetweenClicks) &&
    Number.isFinite(settings.minutesBetweenLoops) &&
    settings.minutesBetweenLoops >= 0 &&
    Number.isFinite(millisecondsBetweenLoops) &&
    Number.isSafeInteger(settings.numberOfLoops) &&
    settings.numberOfLoops > 0 &&
    Number.isFinite(scheduledLoopSpan)
  );
}

function waitChunk(milliseconds: number, signal: AbortSignal): Promise<boolean> {
  if (signal.aborted) return Promise.resolve(false);
  if (milliseconds <= 0) return Promise.resolve(true);

  return new Promise((resolve) => {
    let settled = false;

    const finish = (completed: boolean) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      signal.removeEventListener('abort', onAbort);
      resolve(completed);
    };
    const onAbort = () => finish(false);
    const timer = setTimeout(() => finish(true), milliseconds);

    signal.addEventListener('abort', onAbort, { once: true });
    if (signal.aborted) finish(false);
  });
}

async function wait(milliseconds: number, signal: AbortSignal): Promise<boolean> {
  let remaining = milliseconds;
  while (remaining > 0) {
    const chunk = Math.min(remaining, MAX_TIMER_DELAY_MILLISECONDS);
    const completed = await waitChunk(chunk, signal);
    if (!completed) return false;
    remaining -= chunk;
  }
  return !signal.aborted;
}

export async function runCadence({
  settings,
  signal,
  attempt,
  onProgress,
  now = Date.now,
}: RunCadenceOptions): Promise<CadenceRunResult> {
  if (!isValidCadenceSettings(settings)) {
    throw new RangeError('Invalid cadence settings.');
  }

  const startedAt = now();
  const millisecondsBetweenLoops = settings.minutesBetweenLoops * 60 * 1000;
  const millisecondsBetweenClicks = settings.secondsBetweenClicks * 1000;

  for (let loopNumber = 1; loopNumber <= settings.numberOfLoops; loopNumber += 1) {
    const scheduledStart = startedAt + (loopNumber - 1) * millisecondsBetweenLoops;
    const ready = await wait(Math.max(0, scheduledStart - now()), signal);
    if (!ready) return 'stopped';

    for (let clickNumber = 1; clickNumber <= settings.clicksPerLoop; clickNumber += 1) {
      if (signal.aborted) return 'stopped';

      const progress: CadenceProgress = {
        loopNumber,
        clickNumber,
        totalLoops: settings.numberOfLoops,
        clicksPerLoop: settings.clicksPerLoop,
      };
      onProgress?.(progress);
      attempt(progress);

      if (clickNumber < settings.clicksPerLoop) {
        const continued = await wait(millisecondsBetweenClicks, signal);
        if (!continued) return 'stopped';
      }
    }
  }

  return 'completed';
}
